import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import FeedbackButton from "./components/FeedbackButton";
import BetaWelcomeModal from "./components/BetaWelcomeModal";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Editor from "./pages/Editor";
import ErrorBoundary from "./components/ErrorBoundary";
import SharePage from "./pages/SharePage";
import BestTransformationsPage from "./pages/BestTransformationsPage";
import GalleryPage from "./pages/GalleryPage";
import AdminConsole from "./pages/AdminConsole";
import LocalCodeFinder from "./pages/LocalCodeFinder";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Account from "./pages/Account";

function AppRouter() {
  const location = useLocation();
  // Synchronous detection during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/designs/:id"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Editor />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      {/* V1.9.0 — Owner-only beta admin console */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminConsole />
          </ProtectedRoute>
        }
      />
      {/* V1.7.7 — public read-only share page (no auth required) */}
      <Route path="/share/:slug" element={<SharePage />} />
      <Route path="/best-transformations" element={<BestTransformationsPage />} />
      <Route path="/showcase" element={<BestTransformationsPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      {/* V2.0.11 — public Local Code Finder lead magnet (no auth required) */}
      <Route path="/local-code-finder" element={<LocalCodeFinder />} />
      <Route path="/code-finder" element={<LocalCodeFinder />} />
      {/* V2.0.19 — public Terms of Service / legal disclaimer (no auth required) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      {/* V2.0.50 — public Privacy Policy (no auth required) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      {/* V2.0.21 — Account page (ToS acceptance audit log) */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

// V1.9.0 — Mount the floating Feedback FAB + first-login Welcome modal
// for every authenticated route. Anonymous pages (Landing, /share/:slug)
// don't see either — they're scoped to signed-in beta testers.
function BetaOverlays() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <BetaWelcomeModal />
      <FeedbackButton />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <AppRouter />
            <BetaOverlays />
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "#18181b",
                  border: "1px solid #27272a",
                  color: "#fafafa",
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: "12px",
                  borderRadius: 0,
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
